import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import PlivoSdk from "plivo-browser-sdk";
import { AppContext } from "@vahak/core/dist/app-context";
import { PRODUCTION_ENV } from "@vahak/core/dist/constants/misc";
import { decryptData } from "@vahak/core/dist/methods/encryptData";
import { toast } from "@vahak/core/dist/components/toast/toast";

interface SharedPlivoSdkContextType {
    plivoBrowserSdk?: PlivoSdk;
    isReady: boolean;
}

const SharedPlivoSdkContext = createContext<SharedPlivoSdkContextType>({
    isReady: false
});

export const useSharedPlivoSdk = () => useContext(SharedPlivoSdkContext);

let plivoSdkInitPromise: Promise<PlivoSdk> | null = null;

const getOrCreatePlivoSdk = async (): Promise<PlivoSdk> => {
    if (plivoSdkInitPromise) {
        return plivoSdkInitPromise;
    }

    plivoSdkInitPromise = (async () => {
        const isProd = process.env.NEXT_PUBLIC_REACT_APP_ENV === PRODUCTION_ENV;
        const PlivoBrowserSdk: typeof PlivoSdk = (await import("plivo-browser-sdk")).default;
        const sdk = new PlivoBrowserSdk({
            debug: isProd ? "OFF" : "ALL",
            permOnClick: true,
            enableTracking: false,
            codecs: ["OPUS", "PCMU"],
            enableIPV6: false,
            audioConstraints: { optional: [{ googAutoGainControl: false }] } as any,
            closeProtection: true,
            enableNoiseReduction: true
        });
        sdk.client.setRingToneBack(false);
        return sdk;
    })();

    return plivoSdkInitPromise;
};

export const SharedPlivoSdkProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { plivoCred, companyId } = useContext(AppContext);
    const [plivoBrowserSdk, setPlivoBrowserSdk] = useState<PlivoSdk>();
    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        let cancelled = false;

        getOrCreatePlivoSdk()
            .then((sdk) => {
                if (!cancelled) {
                    setPlivoBrowserSdk(sdk);
                }
            })
            .catch(() => {
                plivoSdkInitPromise = null;
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!plivoBrowserSdk || !plivoCred?.plivo_user_id) {
            return;
        }

        const client = plivoBrowserSdk.client;

        const onLogin = () => setIsReady(true);
        const onLoginFailed = (reason: string) => {
            toast.error(`Plivo ${reason}`);
            setIsReady(false);
        };
        const onLogout = () => setIsReady(false);

        client.on("onLogin", onLogin);
        client.on("onLoginFailed", onLoginFailed);
        client.on("onLogout", onLogout);

        if (client.isLoggedIn) {
            setIsReady(true);
        } else {
            const config = {
                key: process.env.NEXT_PUBLIC_ENC_KEY as string,
                iv: process.env.NEXT_PUBLIC_ENC_IV as string
            };

            try {
                const password = decryptData(plivoCred.plivo_password, config) as string;
                client.login(plivoCred.plivo_user_id, password);
            } catch {
                toast.error("Not registed to use Plvio");
            }
        }

        return () => {
            client.off("onLogin", onLogin);
            client.off("onLoginFailed", onLoginFailed);
            client.off("onLogout", onLogout);
        };
    }, [plivoBrowserSdk, plivoCred]);

    useEffect(() => {
        if (plivoBrowserSdk?.client?.isLoggedIn && !companyId) {
            plivoBrowserSdk.client.logout();
            setIsReady(false);
        }
    }, [companyId, plivoBrowserSdk]);

    return (
        <SharedPlivoSdkContext.Provider value={{ plivoBrowserSdk, isReady }}>{children}</SharedPlivoSdkContext.Provider>
    );
};
